import { PagingControls } from '../../../components/ui/PagingControls/PagingControls';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { SortableHeader } from '../../../components/ui/SortableHeader/SortableHeader';
import { ReportActions } from '../../../components/ui/ReportActions';
import useWorkHoursReport from './useWorkHoursReport';
import Container from '../../../components/ui/Container/Container';
import { Row, Col, Form, Button, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Helpers } from '../../../utils/helpers';
import styles from './WorkHoursReport.module.css';

interface WorkHoursReportProps {
  visible?: boolean;
}

export const WorkHoursReport = ({ visible = true }: WorkHoursReportProps) => {
  const manager = useWorkHoursReport();
  const handleClearFilters = () => {
    if (manager.resetSearch) {
      manager.resetSearch();
    }
  };

  const handleSearch = () => {
    manager.triggerSearch();
  };

  // Helper function to convert Date to YYYY-MM-DD string for date inputs
  const dateToInputValue = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  if (!visible) return <></>;

  return (
    <Container visible={true} className={styles.reportContainer}>
      {/* Make the form wider to accommodate more fields per row */}
      <Row className="justify-content-center">
        <Col xl={10} lg={11} md={12}>
          <div className={styles.filterSection}>
            {/* Filter Header */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Work Hours Report</h5>
            </div>

            {/* Row 1: Work Date (From), Work Date (Thru), CM ID, First Name, Last Name */}
            <Row className="g-2 mb-2">
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Work Date (From)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.workHoursDateFrom)}
                    onChange={(e) => manager.updateFilter('workHoursDateFrom', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Work Date (Thru)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.workHoursDateThru)}
                    onChange={(e) => manager.updateFilter('workHoursDateThru', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>CM ID</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Club Member ID"
                    value={manager.searchParameters.clubMemberId?.toString() || ''}
                    onChange={(e) => manager.updateFilter('clubMemberId', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>First Name</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Member First Name"
                    value={manager.searchParameters.firstName}
                    onChange={(e) => manager.updateFilter('firstName', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Last Name</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Member Last Name"
                    value={manager.searchParameters.lastName}
                    onChange={(e) => manager.updateFilter('lastName', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 2: Entered Date (From), Entered Date (Thru), Updated Date (From), Updated Date (Thru) */}
            <Row className="g-2 mb-2">
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Entered Date (From)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.enteredDateFrom)}
                    onChange={(e) => manager.updateFilter('enteredDateFrom', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Entered Date (Thru)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.enteredDateThru)}
                    onChange={(e) => manager.updateFilter('enteredDateThru', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Updated Date (From)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.updatedDateFrom)}
                    onChange={(e) => manager.updateFilter('updatedDateFrom', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className={styles.compactFormGroup}>
                  <Form.Label className={styles.smallLabel}>Updated Date (Thru)</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={dateToInputValue(manager.searchParameters.updatedDateThru)}
                    onChange={(e) => manager.updateFilter('updatedDateThru', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Search and Clear Buttons */}
            <Row className="mt-2">
              <Col>
                <div className="d-flex gap-2">
                  <Button variant="primary" onClick={handleSearch} disabled={manager.loading}>
                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                    Search
                  </Button>
                  <Button variant="outline-secondary" onClick={handleClearFilters}>
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Clear All
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* Results Section */}
          <div className={styles.resultsSection}>
            <LoadingContainer isLoading={manager.loading}>
              {!manager.hasPerformedSearch && (
                <div className="text-center py-4 text-muted">
                  <p>
                    Please enter search criteria above and click &ldquo;Search&rdquo; to view work
                    hours.
                  </p>
                </div>
              )}
              {manager.hasPerformedSearch && manager.workHoursPagedResults.resultCount === 0 && (
                <div className="text-center py-4 text-muted">
                  No work hours found matching your criteria.
                </div>
              )}
              {manager.workHoursPagedResults.resultCount > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <strong>{manager.workHoursPagedResults.resultCount}</strong> work hours found
                    </div>
                    <ReportActions
                      data={manager.allResults}
                      columns={manager.getReportColumns()}
                      reportTitle="Work Hours Report"
                      csvFileName="workHoursReportExport.csv"
                      searchCriteria={manager.getSearchCriteria()}
                      size="sm"
                      variant="outline-primary"
                    />
                  </div>

                  <Table responsive hover className={styles.resultsTable}>
                    <thead>
                      <tr>
                        <SortableHeader
                          text="WH_ID"
                          sortField="id"
                          active={manager.searchParameters.sortField === 'id'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="CM_ID"
                          sortField="clubMemberId"
                          active={manager.searchParameters.sortField === 'clubMemberId'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Name"
                          sortField="fullName"
                          active={manager.searchParameters.sortField === 'fullName'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Year"
                          sortField="workHoursDateUtc"
                          active={manager.searchParameters.sortField === 'workHoursDateUtc'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Date"
                          sortField="workHoursDateUtc"
                          active={manager.searchParameters.sortField === 'workHoursDateUtc'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Hours"
                          sortField="workHoursAmount"
                          active={manager.searchParameters.sortField === 'workHoursAmount'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Committee"
                          sortField="committee"
                          active={manager.searchParameters.sortField === 'committee'}
                          onClick={manager.changeSort}
                        />
                        <SortableHeader
                          text="Activity"
                          sortField="activity"
                          active={manager.searchParameters.sortField === 'activity'}
                          onClick={manager.changeSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {manager.workHoursPagedResults.results?.map((workHour) => (
                        <tr
                          key={workHour.id}
                          className={styles.clickableRow}
                          onClick={() => window.open(`/members/${workHour.clubMemberId}`, '_blank')}>
                          <td>{workHour.id}</td>
                          <td>{workHour.clubMemberId}</td>
                          <td>{workHour.fullName || 'N/A'}</td>
                          <td>{new Date(workHour.workHoursDateUtc).getFullYear()}</td>
                          <td>{Helpers.toShortDate(workHour.workHoursDateUtc)}</td>
                          <td>{workHour.workHoursAmount.toFixed(2)}</td>
                          <td>{workHour.committee?.codeValue || 'Unknown'}</td>
                          <td>{workHour.activity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <div className="pt-4">
                    <PagingControls
                      page={manager.workHoursPagedResults.currentPage ?? 1}
                      pageCount={manager.workHoursPagedResults.pageCount ?? 1}
                      maxPlusOrMinusPagesToShow={3}
                      onPageChange={manager.changePage}
                    />
                  </div>

                  {/* Report Totals Table */}
                  <div className="d-flex justify-content-end mt-4">
                    <div className={styles.reportTotalsContainer}>
                      <h6 className="mb-3">Report Totals:</h6>
                      <Table size="sm" bordered className={styles.reportTotalsTable}>
                        <thead>
                          <tr>
                            <th>Number of Work Hour Records</th>
                            <th>Distinct Membership Count</th>
                            <th>Total Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className={styles.totalRow}>
                            <td className={styles.centerAlign}>
                              {manager.getWorkHoursTotals().totalRecords}
                            </td>
                            <td className={styles.centerAlign}>
                              {manager.getWorkHoursTotals().distinctMemberships}
                            </td>
                            <td className={styles.rightAlign}>
                              {manager.getWorkHoursTotals().totalHours.toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </LoadingContainer>
          </div>
        </Col>
      </Row>
    </Container>
  );
};
